import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearAllProducts, loadProducts } from '../../../store/actions/admin/products';
import { EventsState } from '../../../store/types';
import { sortProducts } from '../../../store/utils/content-utils';
import { basicSearch, capitalize } from '../../../utils/utils';
import { Attribute, CreateProduct, Product } from "../../../types/working-model";
// import AllCategories from './all-categories';
import AllProducts from './all-products';
import ProductItem from './product-item';
import ProductsHeader from './products-header';
import EditProductsModal from './edit-products';
import useTranslationModules from '../../../i18n/useTranslationModules';

export type catOrProd = "categories" | "products";
export interface attributeMap { title: string, values: string[] }

export const listPageCount = 25;
export const gridPageCount = 15;

export default function ProductsMain(): JSX.Element {
	const products = useSelector((state: EventsState) => state.ProductsReducer.products);
	const tagSet = useSelector((state: EventsState) => state.ProductsReducer.tagSet);
	const token = useSelector((state: EventsState) => state.AuthReducer.token);
	const user = useSelector((state: EventsState) => state.AuthReducer.user);
	const view = useSelector((state: EventsState) => state.ContentReducer.view);

	const [tabView, setTabView] = useState<catOrProd>("products");
	const [sortBy, setSortBy] = useState("");
	const [search, setSearch] = useState('');
	const [page, setPage] = useState(1);
	const [openProductsModal, setOpenProductsModal] = useState(false);
	const [startingProduct, setStartingProduct] = useState<Product | CreateProduct | null>(null);

	const pageSize = (view === "grid") ? gridPageCount : listPageCount;
	const dispatch = useDispatch();

	useTranslationModules({ language: "en" });

	useEffect(() => {
		dispatch(loadProducts(token!, user!.active_channel));
	}, [dispatch, token, user]);

	useEffect(() => {
		dispatch(clearAllProducts());
	}, [dispatch, page]);

	const handleClose = () => {
		setStartingProduct(null);
		setOpenProductsModal(false);
	};
	const handleOpen = (product: Product | CreateProduct) => {
		setStartingProduct(product);
		setOpenProductsModal(true);
	};

	const { displayList, idList, totalPages } = useMemo(() => {
		// Tag and Search Fitler
		const productsSearch = (!!search || !!tagSet.size)
			? products.filter(prod => {
				const { attributes, product_title } = prod;

				const searchMatch = !search || basicSearch(product_title.base, search);
				if (tagSet.size) {
					if (!attributes?.length) { return false; }

					let attrMatch = false;
					let i = 0;
					while (i < attributes.length && !attrMatch) {
						attrMatch = tagSet.has(attributes[i].value.toLocaleUpperCase());
						i++;
					}
					return (attrMatch && searchMatch);
				}
				else { return searchMatch; }
			})
			: products;
		// Sort the resulting list
		const productsSort = sortBy ? sortProducts(productsSearch, sortBy) : productsSearch;

		//paging 
		const totalPages = Math.ceil(productsSort.length / pageSize);
		const indexStart = (page - 1) * pageSize;
		const indexEnd = indexStart + pageSize;
		const productsPage = productsSort.slice(indexStart, indexEnd);

		//making JSX.element list and idList
		return productsPage.reduce((acc, prod) => {
			acc.displayList.push(<ProductItem onEditProduct={handleOpen} key={prod.product} product={prod} />);
			acc.idList.push(prod.product!);
			return acc;
		}, {
			idList: [] as number[],
			displayList: [] as JSX.Element[],
			totalPages
		});
	}, [sortBy, products, tagSet, search, page, pageSize]);

	// capturing and sorting attributes (categories would get captured here too)
	const sortedAttributesList = useMemo(() => {
		const attributes: { [type: string]: Set<string> } = {};
		// let categories: string[] = []
		products.forEach(prod => {
			// categories = [...categories, ...prod.categories]
			prod.attributes?.forEach((attribute: Attribute) => {
				let { value, type } = attribute;
				value = value.toLocaleUpperCase();
				type = capitalize(type);

				if (attributes[type]) { attributes[type].add(value); }
				else { attributes[type] = new Set([value]); }
			});
		});
		return Object.entries(attributes)
			.sort((a, b) => {
				const titleA = a[0];
				const titleB = b[0];
				return titleA < titleB ? -1 : 1;
			})
			.map(attr => (
				{
					title: attr[0],
					values: Array.from(attr[1]).sort((a, b) => a < b ? -1 : 1)
				}
			));
	}, [products]);
	const attributeOptions = useMemo(() => {
		return sortedAttributesList.map(attribute => attribute.title);
	}, [sortedAttributesList]);

	return (
		<main className="wrapper products-container">
			<div className="container">
				<ProductsHeader
					tabViewState={{ tabView, setTabView }}
					sortState={{ sortBy, setSortBy }}
					searchState={{ search, setSearch }}
					idList={idList}
					attributeOptions={attributeOptions}
				/>
				{/* 
				FUTURE DEV: All Categories isnt included in MVP for feature parody. See above note when ready to add this in
				{categoriesView
					? <AllCategories />
					: <AllProducts
						productsList={displayList}
						attributes={sortedAttributesList}
						idList={idList}
						sortState={{ sortBy, setSortBy }}
						pagingState={{ page, setPage, totalPages }}
					/>
				} */}
				<AllProducts
					productsList={displayList}
					attributes={sortedAttributesList}
					idList={idList}
					sortState={{ sortBy, setSortBy }}
					pagingState={{ page, setPage, totalPages }}
				/>
			</div>
			{
				startingProduct && (
					<EditProductsModal
						isOpen={openProductsModal}
						onClose={handleClose}
						startingProduct={startingProduct}
						attributeOptions={attributeOptions}
					/>
				)
			}
		</main>
	);
}